import React from 'react';

const MarketDepth = ({ depth, totalBuyQty = 0, totalSellQty = 0 }) => {
  // Ensure we have arrays of length 5
  // The schema sends: bid_prices: [p1, p2, p3, p4, p5], bid_qtys: [q1, q2, ...], etc.
  
  const levels = [0, 1, 2, 3, 4];
  
  // Safety checks
  const bidPrices = depth?.bid_prices || [];
  const bidQtys = depth?.bid_qtys || [];
  const bidOrders = depth?.bid_orders || [];
  
  const askPrices = depth?.ask_prices || [];
  const askQtys = depth?.ask_qtys || [];
  const askOrders = depth?.ask_orders || [];

  // Debug Log
  console.log("[MarketDepth] Props:", { 
      bidCount: bidPrices.length, 
      firstBid: bidPrices[0],
      totalBuy: totalBuyQty,
      hasDepth: !!depth
  });

  const maxTotal = Math.max((totalBuyQty || 0) + (totalSellQty || 0), 1);
  const buyPercent = ((totalBuyQty || 0) / maxTotal) * 100;

  // Calculate max quantity for the shuttle bars
  const maxVisibleQty = React.useMemo(() => {
     let max = 1;
     for(let i of levels) {
         if (bidQtys[i] > max) max = bidQtys[i];
         if (askQtys[i] > max) max = askQtys[i];
     }
     return max;
  }, [bidQtys, askQtys]); 

  const getBarWidth = (qty) => {
      if (!qty) return '0%';
      return `${Math.min((qty / maxVisibleQty) * 100, 100)}%`;
  };
  
  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-between mb-1 px-1">
        <h2 className="text-[10px] font-black text-fg-2 uppercase tracking-widest">Market Depth</h2>
      </div>

      <div className="bg-bg-2 rounded-lg border border-[var(--divider)] overflow-hidden text-[10px] font-bold font-mono relative">
        
        {/* --- HORIZONTAL VIEW (< lg) --- */}
        <div className="block lg:hidden h-full overflow-y-auto">
            {/* Table Header */}
            <div className="grid grid-cols-6 bg-bg-3/50 p-1.5 text-fg-3 border-b border-[var(--divider)] sticky top-0 z-10">
            <div className="text-center">Ord</div>
            <div className="text-center">Qty</div>
            <div className="text-right pr-2">Bid</div>
            <div className="text-left pl-2">Ask</div>
            <div className="text-center">Qty</div>
            <div className="text-center">Ord</div>
            </div>

            {/* Rows */}
            <div className="flex flex-col">
            {levels.map((i) => (
                <div key={i} className="grid grid-cols-6 py-1 hover:bg-bg-3/30 transition-colors">
                {/* BIDS SIDE */}
                <div className="text-center text-fg-2">
                    {bidOrders[i] || '-'}
                </div>
                <div className="text-center text-profit">
                    {bidQtys[i]?.toLocaleString() || '-'}
                </div>
                <div className="text-right pr-2 text-profit">
                    {bidPrices[i] !== undefined && bidPrices[i] !== null ? bidPrices[i].toFixed(2) : '-'}
                </div>

                {/* ASKS SIDE */}
                <div className="text-left pl-2 text-loss">
                    {askPrices[i] !== undefined && askPrices[i] !== null ? askPrices[i].toFixed(2) : '-'}
                </div>
                <div className="text-center text-loss">
                    {askQtys[i]?.toLocaleString() || '-'}
                </div>
                <div className="text-center text-fg-2">
                    {askOrders[i] || '-'}
                </div>
                </div>
            ))}
            </div>
        </div>

        {/* --- VERTICAL LADDER VIEW (>= lg) --- */}
        <div className="hidden lg:flex flex-col w-full">
             {/* Header */}
             <div className="grid grid-cols-5 bg-bg-3/50 p-1 text-fg-3 border-b border-[var(--divider)] text-[9px] uppercase font-bold tracking-wider">
                <div className="text-center">Ord</div>
                <div className="text-center">Qty</div>
                <div className="text-center text-white">Price</div>
                <div className="text-center">Qty</div>
                <div className="text-center">Ord</div>
            </div>
            
            <div className="flex flex-col w-full">
                {/* ASKS (TOP HALF) - Data on RIGHT */}
                {[...levels].reverse().map((i) => (
                   <div key={`ask-${i}`} className="grid grid-cols-5 h-6 items-center hover:bg-bg-3 transition-colors border-b border-dotted border-[var(--divider)] relative">
                       {/* Left Side (Empty for Asks) */}
                       <div className="col-span-2"></div>

                       {/* Price (Center) */}
                       <div className="text-center text-loss font-bold bg-loss/5 h-full flex items-center justify-center">
                           {askPrices[i] !== undefined && askPrices[i] !== null ? askPrices[i].toFixed(2) : '-'}
                       </div>

                       {/* Right Side (Data for Asks) */}
                       <div className="col-span-2 grid grid-cols-2 relative h-full items-center">
                           {/* Shuttle Bar */}
                           <div 
                                className="absolute left-0 top-1 bottom-1 bg-loss/20 transition-all duration-300 rounded-sm"
                                style={{ width: getBarWidth(askQtys[i]) }}
                           />
                           
                           {/* Qty */}
                           <div className="text-right pr-2 text-loss z-10 font-medium leading-none flex items-center justify-end h-full">
                               {askQtys[i]?.toLocaleString() || ''}
                           </div>
                           
                           {/* Orders */}
                           <div className="text-center text-fg-2 z-10 text-[9px] leading-none flex items-center justify-center h-full">
                               {askOrders[i] || ''}
                           </div>
                       </div>
                   </div> 
                ))}

                {/* SPREAD INDICATOR */}
                <div className="h-px bg-[var(--divider)] w-full" />

                {/* BIDS (BOTTOM HALF) - Data on LEFT */}
                {levels.map((i) => (
                    <div key={`bid-${i}`} className="grid grid-cols-5 h-6 items-center hover:bg-bg-3 transition-colors border-b border-dotted border-[var(--divider)] last:border-b-0 relative">
                       {/* Left Side (Data for Bids) */}
                       <div className="col-span-2 grid grid-cols-2 relative h-full items-center">
                           {/* Shuttle Bar */}
                           <div 
                                className="absolute right-0 top-1 bottom-1 bg-profit/20 transition-all duration-300 rounded-sm"
                                style={{ width: getBarWidth(bidQtys[i]) }} 
                           />

                           {/* Orders */}
                           <div className="text-center text-fg-2 z-10 text-[9px] leading-none flex items-center justify-center h-full">
                               {bidOrders[i] || ''}
                           </div>
                            
                           {/* Qty */}
                           <div className="text-right pr-2 text-profit z-10 font-medium leading-none flex items-center justify-end h-full">
                               {bidQtys[i]?.toLocaleString() || ''}
                           </div>
                       </div>

                       {/* Price (Center) */}
                       <div className="text-center text-profit font-bold bg-profit/5 h-full flex items-center justify-center">
                           {bidPrices[i] !== undefined && bidPrices[i] !== null ? bidPrices[i].toFixed(2) : '-'}
                       </div>

                       {/* Right Side (Empty for Bids) */}
                       <div className="col-span-2"></div>
                   </div> 
                ))}
            </div>
            
            {/* Totals Footer */}
            <div className="shrink-0 grid grid-cols-2 border-t border-[var(--divider)] bg-bg-3/20">
                <div className="p-1 border-r border-[var(--divider)] text-center">
                    <div className="text-[9px] text-fg-3 uppercase font-bold mb-0.5">Total Buy</div>
                    <div className="text-xs text-profit font-black">{totalBuyQty?.toLocaleString() || 0}</div>
                </div>
                <div className="p-1 text-center">
                    <div className="text-[9px] text-fg-3 uppercase font-bold mb-0.5">Total Sell</div>
                    <div className="text-xs text-loss font-black">{totalSellQty?.toLocaleString() || 0}</div>
                </div>
            </div>
        </div>
      </div>

       {/* Ratio Bar */}
      <div className="h-0.5 w-full bg-loss rounded-full overflow-hidden flex shrink-0 mt-0.5">
          <div 
            className="h-full bg-profit transition-all duration-300" 
            style={{ width: `${buyPercent}%` }}
          />
      </div>
    </div>
  );
};

export default MarketDepth;
